import React, { useState, useEffect, useCallback } from 'react';
import './AboutUs.css';
import Lightbox from './Lightbox';

const AboutUs: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  const sliderImages = [
    { src: '/images/about/sinif.jpg', alt: 'Sınıf ortamı' },
    { src: '/images/about/kuran.jpg', alt: 'Kuran eğitimi' },
    { src: '/images/about/oyun.jpg', alt: 'Oyun alanı' },
    { src: '/images/about/aktivite.jpg', alt: 'Aktiviteler' },
    { src: '/images/about/etkinlik1.jpg', alt: 'Anaokulu sınıf etkinliği' },
    { src: '/images/about/etkinlik2.jpg', alt: 'Çocuklar grup çalışması yapıyor' },
    { src: '/images/about/etkinlik3.jpg', alt: 'Oyun temelli öğrenme etkinliği' },
    { src: '/images/about/etkinlik4.jpg', alt: 'Yaratıcı sanat çalışması' },
    { src: '/images/about/etkinlik5.jpg', alt: 'Okul öncesi değerler eğitimi' },
    { src: '/images/about/etkinlik6.jpg', alt: 'Anaokulu bahçe etkinliği' },
  ];

  const goToNext = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % sliderImages.length);
  }, [sliderImages.length]);

  const goToPrev = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + sliderImages.length) % sliderImages.length);
  }, [sliderImages.length]);

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  const openLightbox = () => {
    setLightboxOpen(true);
    setIsPaused(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setIsPaused(false);
  };

  // Auto-play slider
  useEffect(() => {
    if (isPaused || lightboxOpen) return;

    const interval = setInterval(() => {
      goToNext();
    }, 4000); // 4 saniyede bir geçiş

    return () => clearInterval(interval);
  }, [isPaused, lightboxOpen, goToNext]);

  return (
    <section id="about" className="about-section">
      {/* Top Wave */}
      <div className="about-wave-top">
        <svg viewBox="0 0 1440 100" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M0,50 C120,20 240,80 360,50 C480,20 600,80 720,50 C840,20 960,80 1080,50 C1200,20 1320,80 1440,50 L1440,0 L0,0 Z"
                fill="#FFF5F2" />
        </svg>
      </div>

      <div className="about-container">
        {/* Left Side - Image Slider */}
        <div className="about-slider-wrapper">
          <div
            className="about-slider"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
          >
            <div
              className="slider-track"
              style={{ transform: `translateX(-${currentSlide * 100}%)` }}
            >
              {sliderImages.map((image, index) => (
                <div key={index} className="slider-slide" onClick={openLightbox}>
                  <img src={image.src} alt={image.alt} />
                  <div className="slider-overlay">
                    <i className="fas fa-search-plus"></i>
                  </div>
                </div>
              ))}
            </div>

            {/* Navigation Arrows */}
            <button className="slider-arrow slider-arrow-prev" onClick={goToPrev}>
              <i className="fas fa-chevron-left"></i>
            </button>
            <button className="slider-arrow slider-arrow-next" onClick={goToNext}>
              <i className="fas fa-chevron-right"></i>
            </button>

            {/* Dots */}
            <div className="slider-dots">
              {sliderImages.map((_, index) => (
                <button
                  key={index}
                  className={`slider-dot ${currentSlide === index ? 'active' : ''}`}
                  onClick={() => goToSlide(index)}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Right Side - Content */}
        <div className="about-content">
          <p className="about-subtitle">Hakkımızda</p>
          <h2 className="about-title">OKULUMUZ HAKKINDA</h2>

          <p className="about-description">
            Esenyurt İlçe Müftülüğü Albayrak Vakfı bünyesinde, 2017 yılında eğitim hayatına başlayan
            Cennetin Çocukları 4-6 yaş grubu okulumuz; Kur'an-ı Kerim ve değerler eğitimi başta olmak üzere,
            4-6 yaş grubu çocuklarımıza yönelik manevi, sosyal, duygusal ve bilişsel gelişimlerini
            destekleyen bir eğitim modeli uygulamaktadır.
          </p>

          <p className="about-description-secondary">
            Eğitim ortamlarımız, çocukların rahatça öğrenip keşfedebileceği şekilde düzenlenmiştir.
            Cennetin Çocukları 4-6 yaş grubu okulumuz olarak, yarının bilinçli, ahlaklı ve donanımlı bireylerini
            yetiştirmek için çalışıyoruz.
          </p>

          <div className="about-features">
            <div className="features-column">
              <div className="feature-item">
                <span className="feature-icon"></span>
                <span className="feature-text">Özgüven Geliştirme</span>
              </div>
              <div className="feature-item">
                <span className="feature-icon"></span>
                <span className="feature-text">Yaratıcı Aktiviteler</span>
              </div>
            </div>
            <div className="features-column">
              <div className="feature-item">
                <span className="feature-icon"></span>
                <span className="feature-text">Analitik Düşünme</span>
              </div>
              <div className="feature-item">
                <span className="feature-icon"></span>
                <span className="feature-text">Milli ve Manevi Değerler</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Lightbox */}
      <Lightbox
        isOpen={lightboxOpen}
        imageSrc={sliderImages[currentSlide]?.src || ''}
        imageAlt={sliderImages[currentSlide]?.alt || ''}
        onClose={closeLightbox}
        onPrev={goToPrev}
        onNext={goToNext}
        hasPrev={true}
        hasNext={true}
      />
    </section>
  );
};

export default AboutUs;
